import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { API_URL } from '../config';
import {
  colors,
  gradients,
  shadows,
  typography,
  borderRadius,
  getCosmicBackground,
  getCardStyle,
  getButtonStyle,
  getScoreStyle,
} from '../theme';

interface CalculationResult {
  date: string;
  score: number;
  color_code: string;
  explanation: string;
  planetary_factors: string[];
  challenges?: string[];
  opportunities?: string[];
}

interface CrystalRecommendation {
  name: string;
  properties: string[];
  chakra_alignment: string;
  programming_timing: string;
  cleansing_method: string;
  combination_crystals?: string[];
}

interface OptimalPeriod {
  start_date: string;
  end_date: string;
  avg_score: number;
  peak_score: number;
}

const ResultsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [calculation, setCalculation] = useState<any>(null);
  const [results, setResults] = useState<CalculationResult[]>([]);
  const [crystals, setCrystals] = useState<CrystalRecommendation[]>([]);
  const [optimalPeriods, setOptimalPeriods] = useState<OptimalPeriod[]>([]);
  const [selectedTab, setSelectedTab] = useState<'overview' | 'calendar' | 'crystals'>('overview');

  const token = localStorage.getItem('auth_token');

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, id, navigate]);

  const fetchResults = async () => {
    try {
      const response = await fetch(`${API_URL}/calculations/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to load calculation results');
      }

      const data = await response.json();
      setCalculation(data.data.calculation);
      setResults(data.data.results || []);
      setCrystals(data.data.crystal_recommendations || []);
      setOptimalPeriods(data.data.optimal_periods || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        ...getCosmicBackground(),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: colors.text.starWhite,
        fontFamily: typography.fontFamily.heading,
        fontSize: typography.fontSize.xl,
        letterSpacing: typography.letterSpacing.wider,
      }}>
        <span style={{ color: colors.accent.gold, marginRight: '12px' }}>★</span>
        Loading results...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        ...getCosmicBackground(),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}>
        <div style={{ ...getCardStyle('default'), textAlign: 'center', maxWidth: '400px' }}>
          <div style={{ fontSize: '56px', marginBottom: '16px', color: colors.status.error }}>✖</div>
          <h2 style={{
            fontFamily: typography.fontFamily.heading,
            color: colors.status.error,
            margin: '0 0 12px 0',
          }}>Error</h2>
          <p style={{ color: colors.text.secondary, marginBottom: '24px' }}>{error}</p>
          <Link to="/dashboard" style={{ textDecoration: 'none' }}>
            <button style={getButtonStyle('primary')}>
              Back to Dashboard
            </button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      ...getCosmicBackground(),
      padding: '24px 20px',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Star pattern background */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: `
          radial-gradient(2px 2px at 20px 30px, rgba(212, 175, 55, 0.3), transparent),
          radial-gradient(1px 1px at 90px 40px, rgba(255, 255, 255, 0.4), transparent),
          radial-gradient(2px 2px at 130px 80px, rgba(157, 143, 219, 0.2), transparent)
        `,
        backgroundSize: '200px 200px',
        pointerEvents: 'none',
        opacity: 0.5,
        zIndex: 0,
      }} />

      {/* Header */}
      <div style={{ maxWidth: '1000px', margin: '0 auto 28px', position: 'relative', zIndex: 1 }}>
        <Link to="/dashboard" style={{
          color: colors.text.secondary,
          textDecoration: 'none',
          fontSize: typography.fontSize.sm,
        }}>
          ← Back to Dashboard
        </Link>
      </div>

      <div style={{ maxWidth: '1000px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
        {/* Title Card */}
        <div style={{ ...getCardStyle('default'), marginBottom: '24px', textAlign: 'center' }}>
          <h1 style={{
            fontFamily: typography.fontFamily.heading,
            fontSize: typography.fontSize['2xl'],
            fontWeight: typography.fontWeight.bold,
            color: colors.text.starWhite,
            margin: '0 0 12px 0',
            letterSpacing: typography.letterSpacing.wide,
            textTransform: 'capitalize',
          }}>
            <span style={{ color: colors.accent.gold }}>◈</span> {calculation?.decision_type?.replace('_', ' ')} Timing Analysis
          </h1>
          <p style={{ color: colors.text.secondary, margin: 0, fontSize: typography.fontSize.sm }}>
            {calculation && `${new Date(calculation.timeframe_start).toLocaleDateString()} - ${new Date(calculation.timeframe_end).toLocaleDateString()}`}
            {' '}• {results.length} days analyzed
          </p>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
          {[
            { key: 'overview', label: '☆ Overview' },
            { key: 'calendar', label: '☼ Calendar View' },
            { key: 'crystals', label: '♦ Crystal Guidance' }
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setSelectedTab(tab.key as any)}
              style={{
                padding: '14px 24px',
                borderRadius: borderRadius.md,
                border: selectedTab === tab.key ? `2px solid ${colors.accent.gold}` : `1px solid ${colors.border.subtle}`,
                background: selectedTab === tab.key ? 'rgba(212, 175, 55, 0.15)' : 'rgba(74, 63, 138, 0.1)',
                color: selectedTab === tab.key ? colors.accent.gold : colors.text.secondary,
                fontWeight: typography.fontWeight.semibold,
                cursor: 'pointer',
                fontFamily: typography.fontFamily.body,
                transition: 'all 0.3s ease',
                boxShadow: selectedTab === tab.key ? shadows.goldGlow : 'none',
              }}
              dangerouslySetInnerHTML={{ __html: tab.label }}
            />
          ))}
        </div>

        {/* Overview Tab */}
        {selectedTab === 'overview' && (
          <div>
            {/* Optimal Periods */}
            <div style={{ ...getCardStyle('default'), marginBottom: '24px' }}>
              <h2 style={{
                fontFamily: typography.fontFamily.heading,
                fontSize: typography.fontSize.xl,
                color: colors.accent.gold,
                margin: '0 0 20px 0',
                letterSpacing: typography.letterSpacing.wide,
              }}>★ Best Times</h2>
              {optimalPeriods.length === 0 ? (
                <p style={{ color: colors.text.muted }}>No highly favorable periods found in this timeframe.</p>
              ) : (
                <div style={{ display: 'grid', gap: '16px' }}>
                  {optimalPeriods.map((period, idx) => {
                    const scoreColors = getScoreStyle(period.peak_score);
                    return (
                      <div key={idx} style={{
                        padding: '20px 24px',
                        background: scoreColors.bg,
                        borderRadius: borderRadius.md,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        border: `1px solid ${colors.border.subtle}`,
                        flexWrap: 'wrap',
                        gap: '16px',
                      }}>
                        <div>
                          <div style={{
                            fontWeight: typography.fontWeight.semibold,
                            color: colors.text.starWhite,
                            marginBottom: '6px',
                            fontSize: typography.fontSize.base,
                          }}>
                            {idx === 0 ? '\u{1F947}' : idx === 1 ? '\u{1F948}' : '\u{1F949}'}{' '}
                            {new Date(period.start_date).toLocaleDateString()} - {new Date(period.end_date).toLocaleDateString()}
                          </div>
                          <div style={{ fontSize: typography.fontSize.sm, color: colors.text.muted }}>
                            Average Score: {Math.round(period.avg_score)}%
                          </div>
                        </div>
                        <div style={{
                          background: scoreColors.gradient,
                          color: colors.background.midnight,
                          padding: '12px 24px',
                          borderRadius: borderRadius.full,
                          fontWeight: typography.fontWeight.bold,
                          fontSize: typography.fontSize.base,
                        }}>
                          Peak: {period.peak_score}%
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Summary Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '16px' }}>
              {[
                { label: 'Excellent Days', count: results.filter(r => r.score >= 85).length, gradient: gradients.score.excellent },
                { label: 'Good Days', count: results.filter(r => r.score >= 70 && r.score < 85).length, gradient: gradients.score.good },
                { label: 'Fair Days', count: results.filter(r => r.score >= 50 && r.score < 70).length, gradient: gradients.score.fair },
                { label: 'Challenging', count: results.filter(r => r.score < 50).length, gradient: gradients.score.poor }
              ].map((stat, idx) => (
                <div key={idx} style={{ ...getCardStyle('glass'), textAlign: 'center', padding: '24px 16px' }}>
                  <div style={{
                    fontSize: typography.fontSize['3xl'],
                    fontWeight: typography.fontWeight.bold,
                    background: stat.gradient,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}>{stat.count}</div>
                  <div style={{ fontSize: typography.fontSize.sm, color: colors.text.muted }}>{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Calendar Tab */}
        {selectedTab === 'calendar' && (
          <div style={getCardStyle('default')}>
            <h2 style={{
              fontFamily: typography.fontFamily.heading,
              fontSize: typography.fontSize.xl,
              color: colors.accent.gold,
              margin: '0 0 20px 0',
              letterSpacing: typography.letterSpacing.wide,
            }}>☼ Daily Scores</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '10px' }}>
              {results.map((result, idx) => {
                const scoreColors = getScoreStyle(result.score);
                return (
                  <div key={idx} style={{
                    padding: '16px 10px',
                    background: scoreColors.bg,
                    borderRadius: borderRadius.md,
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    border: `1px solid ${colors.border.subtle}`,
                  }}>
                    <div style={{ fontSize: typography.fontSize.xs, color: colors.text.muted, marginBottom: '6px' }}>
                      {new Date(result.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </div>
                    <div style={{
                      fontSize: typography.fontSize['2xl'],
                      fontWeight: typography.fontWeight.bold,
                      color: scoreColors.text,
                    }}>
                      {result.score}
                    </div>
                    <div style={{ fontSize: typography.fontSize.xs, color: scoreColors.text }}>{scoreColors.label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Crystals Tab */}
        {selectedTab === 'crystals' && (
          <div>
            {crystals.length === 0 ? (
              <div style={{ ...getCardStyle('default'), textAlign: 'center', padding: '48px 32px' }}>
                <div style={{ fontSize: '56px', marginBottom: '16px', color: colors.primary.lavender }}>♦</div>
                <h3 style={{
                  fontFamily: typography.fontFamily.heading,
                  color: colors.text.starWhite,
                  margin: '0 0 12px 0',
                  letterSpacing: typography.letterSpacing.wide,
                }}>Crystal Guidance - Premium Feature</h3>
                <p style={{ color: colors.text.muted, marginBottom: '24px', maxWidth: '400px', margin: '0 auto 24px' }}>
                  Upgrade to Premium to receive personalized crystal recommendations for your decisions.
                </p>
                <Link to="/subscription" style={{ textDecoration: 'none' }}>
                  <button style={getButtonStyle('gold')}>
                    Upgrade to Premium
                  </button>
                </Link>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: '20px' }}>
                {crystals.map((crystal, idx) => (
                  <div key={idx} style={getCardStyle('default')}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '24px', flexWrap: 'wrap' }}>
                      <div style={{
                        width: '80px',
                        height: '80px',
                        background: gradients.primaryButton,
                        borderRadius: borderRadius.xl,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '40px',
                        color: colors.text.starWhite,
                        flexShrink: 0,
                      }}>
                        ♦
                      </div>
                      <div style={{ flex: 1, minWidth: '200px' }}>
                        <h3 style={{
                          fontFamily: typography.fontFamily.heading,
                          color: colors.text.starWhite,
                          margin: '0 0 12px 0',
                          letterSpacing: typography.letterSpacing.wide,
                        }}>{crystal.name}</h3>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
                          {crystal.properties.map((prop, i) => (
                            <span key={i} style={{
                              background: 'rgba(157, 143, 219, 0.2)',
                              color: colors.primary.lavender,
                              padding: '6px 14px',
                              borderRadius: borderRadius.full,
                              fontSize: typography.fontSize.xs,
                              border: `1px solid ${colors.border.subtle}`,
                            }}>
                              {prop}
                            </span>
                          ))}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: typography.fontSize.sm }}>
                          <div>
                            <span style={{ color: colors.text.muted }}>Chakra:</span>
                            <span style={{ marginLeft: '6px', color: colors.text.starWhite }}>{crystal.chakra_alignment}</span>
                          </div>
                          <div style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                            <span style={{ color: colors.text.muted }}>Best Programming:</span>
                            <span style={{ marginLeft: '6px', color: colors.text.starWhite }}>{crystal.programming_timing}</span>
                          </div>
                          <div style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                            <span style={{ color: colors.text.muted }}>Cleansing:</span>
                            <span style={{ marginLeft: '6px', color: colors.text.starWhite }}>{crystal.cleansing_method}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '16px', marginTop: '28px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <Link to="/calculate" style={{ textDecoration: 'none' }}>
            <button style={getButtonStyle('gold')}>
              ◈ New Calculation
            </button>
          </Link>
          <Link to="/dashboard" style={{ textDecoration: 'none' }}>
            <button style={getButtonStyle('ghost')}>
              ☆ Dashboard
            </button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResultsPage;
